import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { organizationState } from '../atoms';
import { errorMessageOf } from '../utils';
import { cancelAiProcessingRunAPI, getAiProcessingRunsAPI, getAxiosCancelTokenSource, isCanceledRequestError, retryAiProcessingRunAPI, startAiProcessingRunAPI } from '../common/services';

// A run is live until the backend marks it finished; the AI server's "queued"
// already arrives as INPROGRESS.
const FINISHED_RUN_STATUSES = ['COMPLETED', 'FAILED', 'CANCELLED'];

const isFinishedRun = (run) => FINISHED_RUN_STATUSES.includes(run?.status);

// The backend refreshes a run from the AI server at most every 5s.
const POLL_INTERVAL_MS = 5 * 1000;

export const useAiEnablement = () => {
    const { id: organizationId, aiProcessing } = useRecoilValue(organizationState);
    return {
        organizationId,
        isAiEnabled: !!organizationId && aiProcessing?.enabled === true,
        isAiLocked: !!organizationId && aiProcessing?.enabled !== true
    };
};

const isAiProcessingConflictError = (error) =>
    error?.response?.status === 409 &&
    error?.response?.data?.error?.details?.code === 'AI_PROCESSING_IN_PROGRESS';

// The run finished between the poll and the cancel: nothing to cancel.
const isAiRunAlreadyFinishedError = (error) =>
    error?.response?.status === 409 &&
    error?.response?.data?.error?.details?.code === 'AI_RUN_ALREADY_FINISHED';

/**
 * Tracks the AI processing runs that block the chat for a scope, and owns the
 * state the docs pages render around them.
 *
 * @param {Object} options
 * @param {"organization"|"house"} options.type - Scope to track; "house" needs houseId
 * @param {string} options.houseId - Project id, for type "house"
 * @param {*} options.refreshKey - Re-checks runs whenever this changes, e.g. the active view id
 * @param {Function} options.onDocsChanged - Called when a run finishes, so the caller can reload its docs
 * @param {Function} options.onNotify - Called with a message when a request fails
 */
const useAiProcessing = ({ type = 'organization', houseId, refreshKey, onDocsChanged, onNotify } = {}) => {
    const { organizationId, isAiLocked } = useAiEnablement();

    const [liveRun, setLiveRun] = useState(null);
    const [failedRun, setFailedRun] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    const pollTimeoutRef = useRef(null);
    const cancelTokenSourceRef = useRef(null);
    const liveRunRef = useRef(null);
    const refreshKeyRef = useRef(refreshKey);
    const onDocsChangedRef = useRef(onDocsChanged);
    const onNotifyRef = useRef(onNotify);

    useEffect(() => {
        onDocsChangedRef.current = onDocsChanged;
        onNotifyRef.current = onNotify;
    }, [onDocsChanged, onNotify]);

    // Backend expects exactly one of houseId | organizationId, and nothing is
    // tracked while the org has AI locked.
    const scopeParams = useMemo(() => {
        if (isAiLocked) return null;
        if (type === 'house') return houseId ? { houseId } : null;
        return organizationId ? { organizationId } : null;
    }, [isAiLocked, type, houseId, organizationId]);

    const fetchRuns = useCallback(async () => {
        if (!scopeParams) return null;

        if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel("Operation canceled due to new request.");
        }
        cancelTokenSourceRef.current = getAxiosCancelTokenSource();

        const response = await getAiProcessingRunsAPI(scopeParams, cancelTokenSourceRef.current.token);
        const runs = response.data?.runs || [];
        const live = runs.find((run) => !isFinishedRun(run)) || null;

        // A run that finished — either way — can have changed what the chat and
        // the list can see.
        if (liveRunRef.current && !live) {
            onDocsChangedRef.current && onDocsChangedRef.current();
        }
        liveRunRef.current = live;
        setLiveRun(live);
        setFailedRun(!live && runs[0]?.status === 'FAILED' ? runs[0] : null);
        return live;
    }, [scopeParams]);

    // A status request failing must not trap the user on the loader: fail open,
    // and keep polling so a still-live run re-blocks the chat on recovery.
    const handleFetchError = useCallback((error) => {
        console.error('Error fetching AI processing runs', error);
        if (liveRunRef.current) {
            liveRunRef.current = null;
            setLiveRun(null);
            onNotifyRef.current && onNotifyRef.current("Could not check AI processing status.");
        }
    }, []);

    const scheduleNextPoll = useCallback(() => {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = setTimeout(async () => {
            try {
                const live = await fetchRuns();
                if (live) scheduleNextPoll();
            } catch (error) {
                if (isCanceledRequestError(error)) return;
                handleFetchError(error);
                scheduleNextPoll();
            }
        }, POLL_INTERVAL_MS);
    }, [fetchRuns, handleFetchError]);

    const refreshRuns = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const live = await fetchRuns();
            clearTimeout(pollTimeoutRef.current);
            if (live) scheduleNextPoll();
        } catch (error) {
            if (!isCanceledRequestError(error)) {
                handleFetchError(error);
                scheduleNextPoll();
            }
        } finally {
            setIsRefreshing(false);
        }
    }, [fetchRuns, scheduleNextPoll, handleFetchError]);

    // Picks the run back up under its own id, so the backend can count attempts;
    // only a scope that has never run needs a brand new one.
    const retryRun = useCallback(async () => {
        if (!organizationId) return;

        setIsRetrying(true);
        try {
            if (failedRun?.id) await retryAiProcessingRunAPI(failedRun.id);
            else await startAiProcessingRunAPI({ organizationId, ...(scopeParams?.houseId ? { houseId: scopeParams.houseId } : {}) });

            setFailedRun(null);
            const live = await fetchRuns();
            clearTimeout(pollTimeoutRef.current);
            if (live) scheduleNextPoll();
        } catch (error) {
            if (!isCanceledRequestError(error)) {
                console.error('Error starting AI processing run', error);
                onNotifyRef.current && onNotifyRef.current(errorMessageOf(error, "Could not start AI data processing."));
            }
        } finally {
            setIsRetrying(false);
        }
    }, [organizationId, failedRun, scopeParams, fetchRuns, scheduleNextPoll]);

    // Ends the run that is in progress or keeps failing, and discards its
    // documents, so the chat is usable again while support picks the case up.
    const cancelRun = useCallback(async () => {
        const runId = liveRun?.id || failedRun?.id;
        if (!runId) return;

        setIsCancelling(true);
        try {
            const response = await cancelAiProcessingRunAPI(runId);
            setFailedRun(null);
            liveRunRef.current = null;
            setLiveRun(null);
            clearTimeout(pollTimeoutRef.current);
            onDocsChangedRef.current && onDocsChangedRef.current();
            onNotifyRef.current && onNotifyRef.current(response?.data?.message || "Ticket sent to the support team and help is on the way.", true);
            // Re-sync the progress with the server now that the run is gone.
            await refreshRuns();
        } catch (error) {
            if (isCanceledRequestError(error)) return;
            // Raced with the run finishing — reload instead of reporting a failure.
            if (isAiRunAlreadyFinishedError(error)) {
                refreshRuns();
                return;
            }
            console.error('Error cancelling AI processing run', error);
            onNotifyRef.current && onNotifyRef.current(errorMessageOf(error, "Could not cancel AI data processing."));
        } finally {
            setIsCancelling(false);
        }
    }, [liveRun, failedRun, refreshRuns]);

    // A run can start between polls; the backend then 409s the ask.
    const onAskError = useCallback((error) => {
        if (isAiProcessingConflictError(error)) refreshRuns();
    }, [refreshRuns]);

    useEffect(() => {
        if (!scopeParams) return;

        (async () => {
            try {
                const live = await fetchRuns();
                if (live) scheduleNextPoll();
            } catch (error) {
                if (!isCanceledRequestError(error)) {
                    handleFetchError(error);
                    scheduleNextPoll();
                }
            }
        })();

        return () => {
            clearTimeout(pollTimeoutRef.current);
            if (cancelTokenSourceRef.current) {
                cancelTokenSourceRef.current.cancel("Operation canceled due to unmount.");
            }
            liveRunRef.current = null;
        };
    }, [scopeParams, fetchRuns, scheduleNextPoll, handleFetchError]);

    // e.g. a new/synced project file produced a new active view, which the
    // backend may have just started a run for. The mount effect covers the first.
    useEffect(() => {
        if (refreshKeyRef.current === refreshKey) return;
        refreshKeyRef.current = refreshKey;
        refreshRuns();
    }, [refreshKey, refreshRuns]);

    return {
        isAiLocked,
        isAiProcessing: !!liveRun,
        failedRun,
        showAiState: isAiLocked || !!liveRun || !!failedRun,
        refreshRuns,
        isRefreshing,
        retryRun,
        isRetrying,
        cancelRun,
        isCancelling,
        onAskError
    };
};

export default useAiProcessing;
